import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import { Link } from "react-router-dom";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";

import ClientTeamDialog from "../../ClientTeamDialog";
import AccountAdminsDialog from "../../AccountAdminsDialog";
import AccountTeamMemberDialog from "../../AccountTeamMemberDialog";

import { useViewAccountTeamMembersListQuery } from "../../../../../reactQueries/AppApi/rqHooksForAccountTeamMembers";
import {
  useGetClientCodeListQuery,
  useFileDownloadMutation,
} from "../../../../../reactQueries/commonRQHooks";
import {
  useUploadLogoOfClientAccountMutation,
  useUpdateClientAccountByIdMutation,
  useDeleteClientAccountByIdMutation,
} from "../../../../../reactQueries/AppApi/rqHooksForClientAccount";
import { useGetClientUserListByClientAccountIdQuery } from "../../../../../reactQueries/AppApi/rqHooksForClientUsers";
import ViewAllClientUserForNotiDialog from "../../viewAllUser/ViewAllClientUserForNotiDialog";
import { editClientAccValidation } from "./editClientAccValidation";
import Stack from "@mui/material/Stack";
import { clientAccountViewPath } from "../../../../../routes/routePaths";
import MpgErrorDialog from "../../../../../common/dialog/MpgErrorDialog";
import MpgSuccessDialog from "../../../../../common/dialog/MpgSuccessDialog";
import MpgLoadingDialog from "../../../../../common/dialog/MpgLoadingDialog";
import { saveAs } from "file-saver";
import MpgConfirmDialogForAccAndSubAccount from "../../../../../common/dialog/MpgConfirmDialogForAccAndSubAccount";
import useTrackUserActivity from "../../../../../userActivityTracking/useTrackUserActivity";
import { getGUIDAndExtentionFromFileURL } from "../../../../../hooksAndFunctions/functions/getGUIDAndExtentionFromFileURL";
import { useGetAccountAndSubAccListOnlyForA4Role } from "../../../../../hooksAndFunctions/customHooks/useGetAccountAndSubAccListOnlyForA4Role";
import { getAccessTypes } from "../../../../../hooksAndFunctions/functions/getAccessTypes";
import { useGetLoggedInUserDetails } from "../../../../../hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import MpgClientCodeTransferListDialogForClientAcc from "../../../../../CommonFormComp/MpgClientCodeTransferListForClientAcc/MpgClientCodeTransferListDialogForClientAcc";

import {
  mapLangListToAutocomplete,
  mapCurrencyListToAutocomplete,
  mapClientTypeListToAutocomplete,
  setClientCodes,
} from "../../../../../common/utilities/Helper";
import { getClientAccountCmsFetchers } from "../clientAccountCmsHelper";
import {
  buildEditClientAccountBody,
  buildEditSubmitTransformData,
} from "../../accountSubmitTransformHelper";
import { trackDownloadAndDeleteActivity } from "../../accountActivityTrackingHelper";
import AccountTeamAndAdminsSection from "../../AccountTeamAndAdminsSection";
import ManageAccountSubmitButton from "../../ManageAccountSubmitButton";
import { buildAccountTeamAndAdminsSectionProps } from "../../accountTeamAndAdminsSectionHelper";
import {
  useSupportiveLanguageOptions,
  useSyncAccountAdminsValidation,
} from "../../accountFormCommonHooks";
import EditClientAccountDetailsSection from "../../EditClientAccountDetailsSection";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

const EditClientAccount = ({
  clientAccountDataById,
  LangList,
  CurrencyList,
  ClientTypeList,
  accountAdminListFromAPI,
}) => {
  const { refClientAccountCmsData } = useOutletContext();
  const {
    fetchClientAccountCmsData,
    fetchClientTeamMemberCmsData,
    fetchAccountTeamMemberCmsData,
    fetchAccountAdmnCmsData,
  } = getClientAccountCmsFetchers(refClientAccountCmsData);
  const { id: clientAccountId } = useParams();

  let navigate = useNavigate();

  const userName = JSON.parse(window.sessionStorage.getItem("UserAccount"))?.[
    "name"
  ];

  const { loggedInUser } = useGetLoggedInUserDetails();
  const { id: userId } = loggedInUser;

  const { clientPortalAdminType } = getAccessTypes();
  const { hasA4Access } = clientPortalAdminType;

  /** !!!!!!!! form handling !!!!!!! */
  const { schema } = editClientAccValidation();

  const rhfFormInstance = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      client_acc_name: clientAccountDataById?.ClientAccountName,
    },
    mode: "onChange", 
    reValidateMode: "onChange"
  });

  const { setValue, watch } = rhfFormInstance;
  const selectedPrimaryLang = watch("primary_lang");

  /** !!!!!!!! table state handling !!!!!!! */
  const [clientTeamTableData, setClientTeamTableData] = useState([]);
  const [accountTeamMemberTableData, setAccountTeamMemberTableData] = useState(
    []
  );
  const [accountAdminTableData, setAccountAdminTableData] = useState([]);
  const [wasAdminEdited, setWasAdminEdited] = useState(false);

  /** !!!!!!!! dialog state handling !!!!!!! */
  const [openClientTeamDialog, setOpenClientTeamDialog] = useState(false);
  const [openAccountTeamDialog, setOpenAccountTeamDialog] = useState(false);
  const [openAccountAdminDialog, setOpenAccountAdminDialog] = useState(false);

  const [openClientAccCodeDialog, setOpenClientAccCodeDialog] = useState(false);

  const [openAllClientUserNotiDialog, setOpenAllClientUserNotiDialog] =
    useState(false);

  /** !!!!!!!! all API state handling !!!!!!! */
  const {
    mutate: downloadLogo,
    isError: isErrorOnDownload,
    isSuccess: isSuccessOnDownload,
    data: dataOnDownload,
  } = useFileDownloadMutation();

  /**client code list */
  const {
    isSuccess: isSuccessOnClientCodeList,
    data: ClientCodeListData,
    isFetching: isFetchingOnClientCodeList,
  } = useGetClientCodeListQuery();

  /**API calls to UpdateTeamMember */
  const {
    mutate: updateClientAccount,
    isError: isErrorOnUpdateCA,
    isLoading: isLoadingOnUpdateCA,
    isSuccess: isSuccessOnUpdateCA,
    error: errorOnUpdateCA,
  } = useUpdateClientAccountByIdMutation();

  const {
    mutate: uploadLogo,
    isError: isErrorOnLogoUpload,
    isLoading: isLoadingOnLogoUpload,
    isSuccess: isSuccessOnLogoUpload,
    data: dataOnLogoUpload,
  } = useUploadLogoOfClientAccountMutation();

  const {
    mutate: deleteClientAccountById,
    isError: isErrorOnDeleteCA,
    isLoading: isLoadingOnDeleteCA,
    isSuccess: isSuccessOnDeleteCA,
    variables: variablesOnDeleteCA,
  } = useDeleteClientAccountByIdMutation();

  const { data: accountTeamMembersList } = useViewAccountTeamMembersListQuery();

  const {
    isSuccess: isSuccessOnGetClientUsers,
    data: dataOnGetClientUsers,
    refetch: refetchOnGetClientUsers,
  } = useGetClientUserListByClientAccountIdQuery({ clientAccountId });

  /** !!!!!!!! autocomplete option lists !!!!!!! */
  const primaryLangAutocompleteList = mapLangListToAutocomplete(LangList);

  const currencyAutocompleteList = mapCurrencyListToAutocomplete(CurrencyList);

  const ClientTypeAutocompleteList =
    mapClientTypeListToAutocomplete(ClientTypeList);

  const supportiveLangAutocompleteList = useSupportiveLanguageOptions({
    LangList,
    selectedPrimaryLang,
  });

  useSyncAccountAdminsValidation({
    rhfFormInstance,
    accountAdminTableData,
  });

  /**get languages values */
  const clientAccLangList = clientAccountDataById?.AccountLanguages;

  const [existingPrimLang, setExistingPrimLang] = useState({});
  const [existingSupportedLang, setExistingSupportedLang] = useState([]);

  useEffect(() => {
    if (!!clientAccLangList && !!clientAccLangList.length) {
      let prim = {};
      let supportive = [];

      clientAccLangList.forEach((eachLang) => {
        const foundLang =
          !!LangList &&
          LangList.find((item) => item.Id === eachLang.LanguageID);

        if (!!foundLang) {
          if (!!eachLang.IsPrimary) {
            prim = foundLang;
          } else {
            supportive.push(foundLang);
          }
        }
      });
      setExistingPrimLang(prim);
      setExistingSupportedLang(supportive);
    } else {
      setExistingPrimLang({});
      setExistingSupportedLang([]);
    }
  }, [clientAccLangList, LangList]);

  /**setting prim lang default*/
  useEffect(() => {
    if (!!existingPrimLang.Id) {
      const existingPrimLangDefaultList = {
        id: existingPrimLang.Id,
        selectOptionLabel: `${existingPrimLang.LanguageCode}-${existingPrimLang.LanguageDescription}`,
        selectOptionValue: existingPrimLang.Id,
      };
      // console.log(
      //   existingPrimLang.Id,
      //   "primary_lang::",
      //   existingPrimLangDefaultList
      // );
      setValue("primary_lang", existingPrimLangDefaultList || "", {
        shouldValidate: true,
      });
    }
  }, [existingPrimLang]);

  /**setting supported lang default*/
  useEffect(() => {
    if (!!existingSupportedLang.length) {
      const existingSupportedLangList =
        !!existingSupportedLang &&
        existingSupportedLang.map((item) => {
          return {
            id: item.Id,
            selectOptionLabel: `${item.LanguageCode}-${item.LanguageDescription}`,
            selectOptionValue: item.Id,
          };
        });
      setValue("supported_lang", existingSupportedLangList || [], {
        shouldValidate: true,
      });
    }
  }, [existingSupportedLang]);

  /** !!!!!!!!! filtering Admin list on Permissions !!!!!!!!!! */
  /**for client accounts, admins with only A1 and A4 permisions allowed */
  const [filteredAdmnList, setFilteredAdmnList] = useState([]);
  useEffect(() => {
    if (!!accountAdminListFromAPI && !!accountAdminListFromAPI.length) {
      let newList = [];
      const regExp = /A1|A2|A4|Enterprise Admin/;
      accountAdminListFromAPI.forEach((item) => {
        if (item?.permissionRole?.search(regExp) !== -1) {
          newList.push(item);
        }
      });
      setFilteredAdmnList(newList);
    }
  }, [accountAdminListFromAPI]);

  /** !!!!!!! grabing data from client code transfer list !!!!!! */
  const [finalCodeList, setFinalCodeList] = useState([]);
  useEffect(() => {
    /**adding existing codes to final list which is in right table */
    if (!!clientAccountDataById?.ClientAccountCodes) {
      const existingCodeList = clientAccountDataById?.ClientAccountCodes.map(
        (item) => {
          return {
            clientCode: item.clientCode,
            clientCodeName: item.clientCodeName,
          };
        }
      );
      setFinalCodeList(existingCodeList);
    }
  }, [clientAccountDataById?.ClientAccountCodes]);
  /**setting default in code box */
  useEffect(() => {
    setClientCodes(finalCodeList, setValue);
  }, [finalCodeList, setValue]);

  /**setting default in Currency Dropdown */
  useEffect(() => {
    if (!!clientAccountDataById?.CurrencyId && !!CurrencyList) {
      const existingCurrencyDefaultList =
        !!CurrencyList &&
        CurrencyList.filter(
          (item) => item.Id == clientAccountDataById?.CurrencyId
        ).map((item) => {
          return {
            id: item.Id,
            selectOptionLabel: `${item.CurrencyCode}`,
            selectOptionValue: item.Id,
          };
        });
      setValue("CurrencyId", existingCurrencyDefaultList[0] || "", {
        shouldValidate: true,
      });
    } else {
      setValue("CurrencyId", "");
    }
  }, [clientAccountDataById?.CurrencyId, CurrencyList]);

  useEffect(() => {
    if (clientAccountDataById?.DataClassificationKey && ClientTypeList) {
      const existingClientTypeDefaultList = ClientTypeList.filter(
        (item) =>
          item.DataClassificationKey ==
          clientAccountDataById.DataClassificationKey
      ).map((item) => ({
        id: item.DataClassificationKey,
        selectOptionLabel: `${item.MasterDataClassificationName}`,
        selectOptionValue: item.DataClassificationKey,
      }));
      setValue("ClientType", existingClientTypeDefaultList[0] || "", {
        shouldValidate: true,
      });
    } else {
      setValue("ClientType", "");
    }
  }, [clientAccountDataById?.DataClassificationKey, ClientTypeList]);

  /** !!!!!!!!!!populating View all client user dialog with only CL1 CL2 CL9 client users !!!!!!!!!!!!! */
  /**new gl */
  const [filteredCL1CL2ClientUsers, setFilteredCL1CL2ClientUsers] = useState(
    []
  );
  useEffect(() => {
    if (!!isSuccessOnGetClientUsers && !!dataOnGetClientUsers) {
      let newList = [];
      const regExp = /CL1|CL2|CL9/;
      dataOnGetClientUsers.forEach((item) => {
        if (item?.PermissionCode.search(regExp) !== -1) {
          newList.push(item);
        }
      });
      setFilteredCL1CL2ClientUsers(newList);
    }
  }, [dataOnGetClientUsers, isSuccessOnGetClientUsers]);

  /** !!!!!!! populating existing data in tables for this client account !!!!!! */
  useEffect(() => {
    if (!!clientAccountDataById) {
      /** */
      const existingClientUsers = clientAccountDataById?.ClientUserTeams;
      if (!!existingClientUsers) {
        /**only required CL1|CL2|CL9 for client account */
        let newList = [];
        const regExp = /CL1|CL2|CL9/;
        existingClientUsers?.forEach((item) => {
          if (item?.PermissionCode.search(regExp) !== -1) {
            newList.push(item);
          }
        });
        setClientTeamTableData(newList || []);
      }

      /** */
      const existingAccTeamMemberList = clientAccountDataById?.AccountUserTeams;
      if (!!existingAccTeamMemberList) {
        let list = [];
        existingAccTeamMemberList &&
          existingAccTeamMemberList.forEach((item) => {
            list.push({
              teamMemberId: item.StaffId,
              name: `${item.FirstName} ${item.LastName}`,
              email: item.EmailAddress,
              contact: item.ContactNo,
              jobPosition: item.JobPosition,
              bioId: item.BioId,
              bioTitle: item.TemplateTitle,
            });
          });
        setAccountTeamMemberTableData(list);
      }
    }
  }, [clientAccountDataById]);

  useEffect(() => {
    const existingClientadmins = clientAccountDataById?.ClientAccountAdmins;

    if (!!existingClientadmins) {
      let requiredAdminList = [];

      existingClientadmins.forEach((eachAdmin) => {
        const foundItem =
          !!accountAdminListFromAPI &&
          accountAdminListFromAPI.find(
            (item) => item.adminId === eachAdmin.adminId
          );

        if (!!foundItem) {
          requiredAdminList.push(foundItem);
        }
      });
      setAccountAdminTableData(requiredAdminList);
    }
  }, [clientAccountDataById, accountAdminListFromAPI]);

  /**remove logo */
  const [removeLogo, setRemoveLogo] = useState(false);

  /** !!!!!!!! submit handling !!!!!!! */
  const [bodyDataForUpdate, setBodyDataForUpdate] = useState({});
  const onSubmit = (data) => {
    const {
      langList,
      codeList,
      clientUserList,
      accountTeamMembList,
      clientAccAdminList,
    } = buildEditSubmitTransformData({
      data,
      finalCodeList,
      clientTeamTableData,
      accountTeamMemberTableData,
      accountAdminTableData,
      codeKey: "ClientCode",
      codeNameKey: "ClientCodeName",
    });

    const body = buildEditClientAccountBody({
      data,
      clientAccountId,
      updatedBy: userName,
      langList,
      clientUserList,
      accountTeamMembList,
      clientAccAdminList,
      codeList,
      removeLogo,
      currentLogo: clientAccountDataById?.Logo,
    });
    setBodyDataForUpdate(body);


    if (!!data.logo_img[0]) {
      const file = data.logo_img[0];
      uploadLogo({ file });
    } else {
      updateClientAccount({ body, clientAccountId });
    }

    // console.log("subForm", data)
  };
  useEffect(() => {
    if (!!isSuccessOnLogoUpload) {
      const storedFileURL = dataOnLogoUpload.data.FileUrl;
      const bodyData = {
        ...bodyDataForUpdate,
        Logo: storedFileURL,
      };
      updateClientAccount({ body: bodyData, clientAccountId });
    }
  }, [isSuccessOnLogoUpload]);

  useEffect(() => {
    if (!!isSuccessOnUpdateCA || !!isSuccessOnDeleteCA) {
      navigate(clientAccountViewPath);
    }
  }, [isSuccessOnUpdateCA, isSuccessOnDeleteCA]);

  /** states from view All Client users */
  const [emailNotiSuccessFromViewDialog, setEmailNotiSuccessFromViewDialog] =
    useState(false);
  const [emailNotiErrorFromViewDialog, setEmailNotiErrorFromViewDialog] =
    useState(false);
  const [emailNotiLoadingFromViewDialog, setEmailNotiLoadingFromViewDialog] =
    useState(false);

  const [
    userActivationSuccessFromViewDialog,
    setUserActivationSuccessFromViewDialog,
  ] = useState(false);
  const [
    userActivationErrorFromViewDialog,
    setUserActivationErrorFromViewDialog,
  ] = useState(false);
  const [
    userActivationLoadingFromViewDialog,
    setUserActivationLoadingFromViewDialog,
  ] = useState(false);

  /**handling and processing for file download */
  const [fileDetails, setFileDetails] = useState({});
  /**{
   *  fileGUID:"dsdsdsdsdsdd",
   * fileExtension:"png",
   * } */
  useEffect(() => {
    if (!!clientAccountDataById?.Logo) {
      const file = clientAccountDataById?.Logo;

      const splitFileURL = file.split("/");

      const fileGUIDWithExtension = splitFileURL.at(-1);

      const fileGUIDWithExtensionSplit = fileGUIDWithExtension.split(".");

      const fileGUID = fileGUIDWithExtensionSplit[0];
      const fileExtension = fileGUIDWithExtensionSplit[1];

      setFileDetails({
        fileGUID,
        fileExtension,
      });
    } else {
      setFileDetails({});
    }
  }, [clientAccountDataById?.Logo]);

  useEffect(() => {
    if (!!isSuccessOnDownload) {
      saveAs(
        dataOnDownload,
        `${clientAccountDataById?.ClientAccountName}_logo`
      );
    }
  }, [isSuccessOnDownload]);

  /**
   * disable fields and button
   */
  const { clientAccountIdListForA4Role } =
    useGetAccountAndSubAccListOnlyForA4Role({ userId });

  /**disable update button  */
  const [disabledUpdateButton, setDisabledUpdateButton] = useState(false);
  useEffect(() => {
    /**if user has A4 role and not admin for this client sub account */
    if (
      !!hasA4Access &&
      !clientAccountIdListForA4Role.includes(clientAccountDataById?.Id)
    ) {
      setDisabledUpdateButton(true);
    } else {
      /**if client account is deactivated  */
      if (!clientAccountDataById?.IsActive) {
        setDisabledUpdateButton(true);
      } else {
        setDisabledUpdateButton(false);
      }
    }
  }, [hasA4Access, clientAccountIdListForA4Role, clientAccountDataById]);

  /**disable Deactivate/Reactivate button */
  const [
    disabledDeactivateReactivateButton,
    setDisabledDeactivateReactivateButton,
  ] = useState(false);

  useEffect(() => {
    /** a4 role where user is not admin for any clientAcc, then disable this button */
    if (
      !!hasA4Access &&
      !clientAccountIdListForA4Role.includes(clientAccountDataById?.Id)
    ) {
      setDisabledDeactivateReactivateButton(true);
    } else {
      setDisabledDeactivateReactivateButton(false);
    }
  }, [hasA4Access, clientAccountIdListForA4Role]);

  /**confirm dialog state on Activation/Deactivation action */
  const [
    openMpgConfirmDialogForAccAndSubAccount,
    setOpenMpgConfirmDialogForAccAndSubAccount,
  ] = useState(false);

  /**tracking user Activity */
  const { trackActivity } = useTrackUserActivity();
  useEffect(() => {
    trackDownloadAndDeleteActivity({
      trackActivity,
      isSuccessOnDownload,
      downloadItemId: fileDetails?.fileGUID,
      isSuccessOnDeleteCA,
      isDeleteMode: variablesOnDeleteCA?.isDeleteMode,
      entityId: clientAccountId,
    });

    if (!!isSuccessOnLogoUpload) {
      const { fileGUID } = getGUIDAndExtentionFromFileURL({
        fileURL: dataOnLogoUpload?.data?.FileUrl,
      });

      trackActivity({
        activitySuccess: !!isSuccessOnLogoUpload,
        actionType: "Uploaded",
        itemId: fileGUID || "",
      });
    }
  }, [
    isSuccessOnDownload,
    isSuccessOnDeleteCA,
    variablesOnDeleteCA,
    clientAccountId,
    isSuccessOnLogoUpload,
  ]);

  const accountTeamAndAdminsSectionProps = buildAccountTeamAndAdminsSectionProps({
    WrapperComponent: Item,
    cmsComponents: refClientAccountCmsData?.Components,
    fetchClientTeamMemberSelectLabel: fetchClientTeamMemberCmsData(
      "ClientTeamMembers.Select"
    ),
    fetchAccountTeamMemberSelectLabel: fetchAccountTeamMemberCmsData(
      "AccountTeamMembers.Select"
    ),
    fetchAccountAdminSelectLabel: fetchAccountAdmnCmsData("AccountAdmins.Select"),
    setOpenClientTeamDialog,
    setOpenAccountTeamDialog,
    setOpenAccountAdminDialog,
    clientTeamTableData,
    setClientTeamTableData,
    accountTeamMemberTableData,
    setAccountTeamMemberTableData,
    accountAdminTableData,
    setAccountAdminTableData,
    rhfFormInstance,
    wasAdminEdited,
    setWasAdminEdited,
  });

  return (
    <Box sx={{ width: "100%" }}>
      {/* <CommonButtonHeader /> */}
      <Box sx={{ width: "100%" }}>
        <Typography variant="h5" align="center" sx={{ fontWeight: "bolder" }}>
          {fetchClientAccountCmsData("ClientAccountDetails.EditClientAccounts")}
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          m={1}
        >
          <Button
            variant="outlined"
            size="small"
            component={Link}
            to={clientAccountViewPath}
          >
            {fetchClientAccountCmsData("ClientAccountDetails.ClientAccounts")}
          </Button>

          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setOpenAllClientUserNotiDialog(true);
            }}
          >
            {fetchClientAccountCmsData("ClientAccountDetails.ViewAll")}
          </Button>

          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setOpenMpgConfirmDialogForAccAndSubAccount(true);
            }}
            disabled={!!disabledDeactivateReactivateButton}
          >
            {!!clientAccountDataById?.IsActive
              ? fetchClientAccountCmsData("ClientAccountDetails.Deactivate")
              : fetchClientAccountCmsData("ClientAccountDetails.Reactivate")}
          </Button>
        </Stack>

        <Box
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={rhfFormInstance.handleSubmit(onSubmit)}
        >
          <EditClientAccountDetailsSection
            WrapperComponent={Item}
            fetchClientAccountCmsData={fetchClientAccountCmsData}
            rhfFormInstance={rhfFormInstance}
            primaryLangAutocompleteList={primaryLangAutocompleteList || []}
            supportiveLangAutocompleteList={supportiveLangAutocompleteList || []}
            currencyAutocompleteList={currencyAutocompleteList || []}
            clientTypeAutocompleteList={ClientTypeAutocompleteList || []}
            selectedPrimaryLang={selectedPrimaryLang}
            removeLogo={removeLogo}
            setRemoveLogo={setRemoveLogo}
            clientAccountDataById={clientAccountDataById}
            fileDetails={fileDetails}
            downloadLogo={downloadLogo}
            isFetchingOnClientCodeList={isFetchingOnClientCodeList}
            isSuccessOnClientCodeList={isSuccessOnClientCodeList}
            onOpenClientAccCodeDialog={() => {
              setOpenClientAccCodeDialog(true);
            }}
          />
          <br />
          <AccountTeamAndAdminsSection {...accountTeamAndAdminsSectionProps} />

          <ManageAccountSubmitButton
            label={fetchClientAccountCmsData(
              "ClientAccountDetails.UpdateClientAccount"
            )}
            disabled={disabledUpdateButton}
          />
        </Box>

        {/**all Dialogs */}
        <ClientTeamDialog
          open={openClientTeamDialog}
          setOpen={setOpenClientTeamDialog}
          clientTeamTableData={clientTeamTableData || []}
          setClientTeamTableData={setClientTeamTableData}
          clientUserListFromAPI={filteredCL1CL2ClientUsers || []}
        />
        <AccountTeamMemberDialog
          open={openAccountTeamDialog}
          setOpen={setOpenAccountTeamDialog}
          accountTeamMemberTableData={accountTeamMemberTableData || []}
          setAccountTeamMemberTableData={setAccountTeamMemberTableData}
          accountTeamMembersListFromAPI={accountTeamMembersList || []}
        />
        <AccountAdminsDialog
          open={openAccountAdminDialog}
          setOpen={setOpenAccountAdminDialog}
          accountAdminTableData={accountAdminTableData || []}
          setAccountAdminTableData={setAccountAdminTableData}
          accountAdminListFromAPI={filteredAdmnList || []}
        />
      </Box>

      <MpgClientCodeTransferListDialogForClientAcc
        transferDialogTitle={fetchClientAccountCmsData(
          "ClientAccountDetails.SelectClientCodes"
        )}
        leftTableTitle={fetchClientAccountCmsData(
          "ClientAccountDetails.AvailableClientCodes"
        )}
        rightTableTitle={fetchClientAccountCmsData(
          "ClientAccountDetails.SelectedClientCodes"
        )}
        open={openClientAccCodeDialog}
        setOpen={setOpenClientAccCodeDialog}
        grabRightTableData={setFinalCodeList}
        leftTableDataToPop={ClientCodeListData || []}
        rightTableDataToPop={finalCodeList || []}
        clientAccountDataById={clientAccountDataById || {}}
        whereUsed={"Edit"}
      />

      <ViewAllClientUserForNotiDialog
        open={openAllClientUserNotiDialog}
        setOpen={setOpenAllClientUserNotiDialog}
        clientAccOrSubAccName={clientAccountDataById?.ClientAccountName || ""}
        clientUserListFromAPI={filteredCL1CL2ClientUsers || []}
        refetchListFunc={refetchOnGetClientUsers}
        setEmailNotiSuccessFromViewDialog={setEmailNotiSuccessFromViewDialog}
        setEmailNotiErrorFromViewDialog={setEmailNotiErrorFromViewDialog}
        setEmailNotiLoadingFromViewDialog={setEmailNotiLoadingFromViewDialog}
        setUserActivationSuccessFromViewDialog={
          setUserActivationSuccessFromViewDialog
        }
        setUserActivationErrorFromViewDialog={
          setUserActivationErrorFromViewDialog
        }
        setUserActivationLoadingFromViewDialog={
          setUserActivationLoadingFromViewDialog
        }
        isDeactivated={!clientAccountDataById?.IsActive}
      />

      {/** error dialog on update action */}
      <MpgErrorDialog
        open={
          !!isErrorOnUpdateCA ||
          !!isErrorOnLogoUpload ||
          !!isErrorOnDeleteCA ||
          !!emailNotiErrorFromViewDialog ||
          !!userActivationErrorFromViewDialog ||
          !!isErrorOnDownload
        }
        title={`${
          !!isErrorOnUpdateCA &&
          errorOnUpdateCA?.response?.data === "AccountNameExists"
            ? fetchClientAccountCmsData(
                "ClientAccountDetails.ValidationMsgAccountNameExists"
              )
            : ""
        }`}
        content={`${
          !!isErrorOnUpdateCA &&
          errorOnUpdateCA?.response?.data === "AccountNameExists"
            ? fetchClientAccountCmsData(
                "ClientAccountDetails.ValidationMsgDuplicateAccountName"
              ).replace("#CLIENT_ACCOUNT_NAME", `${watch("client_acc_name")}`)
            : ""
        }`}
      />

      <MpgSuccessDialog
        open={
          !!emailNotiSuccessFromViewDialog ||
          !!userActivationSuccessFromViewDialog
        }
      />
      <MpgLoadingDialog
        open={
          !!emailNotiLoadingFromViewDialog ||
          !!userActivationLoadingFromViewDialog ||
          !!isLoadingOnUpdateCA ||
          !!isLoadingOnLogoUpload ||
          !!isLoadingOnDeleteCA
        }
      />

      {/** confirm dialog on delete action */}
      {!!clientAccountDataById?.IsActive && (
        <MpgConfirmDialogForAccAndSubAccount
          open={openMpgConfirmDialogForAccAndSubAccount}
          setOpen={setOpenMpgConfirmDialogForAccAndSubAccount}
          actionType={
            !!clientAccountDataById?.IsActive
              ? "DeactivateAccount"
              : "ActivateAccount"
          }
          handlerOnDeactivation={() => {
            const id = clientAccountDataById?.Id;
            const isActive = clientAccountDataById?.IsActive;
            if (!!isActive) {
              deleteClientAccountById({
                clientAccountId: id,
                isDeleteMode: true,
                isClientAccount: true,
                isActivateAll: false,
              });
            }
          }}
        />
      )}
      {!clientAccountDataById?.IsActive && (
        <MpgConfirmDialogForAccAndSubAccount
          open={openMpgConfirmDialogForAccAndSubAccount}
          setOpen={setOpenMpgConfirmDialogForAccAndSubAccount}
          actionType={
            !!clientAccountDataById?.IsActive
              ? "DeactivateAccount"
              : "ActivateAccount"
          }
          handlerOnAccountOnlyActivation={() => {
            const id = clientAccountDataById?.Id;
            const isActive = clientAccountDataById?.IsActive;
            if (!isActive) {
              deleteClientAccountById({
                clientAccountId: id,
                isDeleteMode: false,
                isClientAccount: true,
                isActivateAll: false,
              });
            }
          }}
          handlerOnAllActivation={() => {
            const id = clientAccountDataById?.Id;
            const isActive = clientAccountDataById?.IsActive;
            if (!isActive) {
              deleteClientAccountById({
                clientAccountId: id,
                isDeleteMode: false,
                isClientAccount: true,
                isActivateAll: true,
              });
            }
          }}
        />
      )}
    </Box>
  );
};

export default EditClientAccount;
